import {
    computed,
    nextTick,
    onScopeDispose,
    ref,
    unref,
    watch,
    type ComputedRef,
} from "vue";
import { isAbortError, normalizeError } from "./errors";
import {
    DEFAULT_CACHE_TIME,
    queryCache,
    serializeQueryKey,
    type CacheEntry,
} from "./queryCache";
import type {
    QueryError,
    QueryKey,
    QueryOptions,
    QueryStatus,
    UseQueryReturn,
} from "./types";

const DEFAULT_STALE_TIME = 0;

const resolveEnabled = (enabled?: unknown): boolean => {
    if (enabled === undefined) return true;
    return Boolean(unref(enabled));
};

const resolveKey = (key: QueryOptions<unknown>["key"]): QueryKey | null => {
    const raw = unref(key);
    if (raw === null || raw === undefined) {
        return null;
    }
    if (Array.isArray(raw)) {
        // Разрешаем null в массиве (для опциональных параметров), но не undefined
        if (raw.some((item) => item === undefined)) {
            return null;
        }
        return raw as QueryKey;
    }

    return raw as QueryKey;
};

const resolveInitialData = <T>(initialData: QueryOptions<T>["initialData"]): T | null => {
    const value = unref(initialData);
    return value ?? null;
};

const setStateFromCache = <T>(
    cached: CacheEntry<T> | undefined,
    staleTime: number,
    status: { value: QueryStatus },
    data: { value: T | null },
    error: { value: QueryError | null },
): boolean => {
    if (!cached || cached.data === undefined) {
        return false;
    }

    data.value = (cached.data as T) ?? null;
    error.value = null;
    const fresh = cached.updatedAt > 0 && queryCache.isFresh(cached, staleTime);
    status.value = fresh ? "success" : "loading";
    return fresh;
};

export function createQuery<TData>(options: QueryOptions<TData>): UseQueryReturn<TData> {
    const data = ref<TData | null>(resolveInitialData(options.initialData));
    const status = ref<QueryStatus>(data.value === null ? "idle" : "success");
    const error = ref<QueryError | null>(null);

    const isLoading: ComputedRef<boolean> = computed(() => status.value === "loading");
    const isSuccess: ComputedRef<boolean> = computed(() => status.value === "success");
    const isError: ComputedRef<boolean> = computed(() => status.value === "error");

    let lastSerializedKey: string | null = null;
    let lastKey: QueryKey | null = null;
    let stopWatch: (() => void) | null = null;
    let unsubscribeInvalidation: (() => void) | null = null;
    /**
     * Контроллер ЖИВОГО запроса этого подписчика. Смена ключа и смерть scope
     * его обрывают: ответ на прошлые фильтры не нужен, и сеть не должна его
     * дотягивать. Fetcher, который сигнал не читает, всё равно защищён ниже —
     * поздний ответ не записывается в чужой ключ.
     */
    let activeController: AbortController | null = null;

    const abortActive = (): void => {
        activeController?.abort();
        activeController = null;
    };

    /** Ответ относится к ключу, на который подписчик смотрит СЕЙЧАС. */
    const isCurrentKey = (key: QueryKey): boolean =>
        serializeQueryKey(key) === lastSerializedKey;

    const run = async (force = false, targetKey?: QueryKey | null): Promise<void> => {
        const key = targetKey ?? resolveKey(options.key);
        if (!key) {
            return;
        }

        const staleTime = options.staleTime ?? DEFAULT_STALE_TIME;
        const cacheTime = options.cacheTime ?? DEFAULT_CACHE_TIME;
        const cached = queryCache.get<TData>(key);

        if (!force && cached) {
            const alreadyFresh = setStateFromCache(cached, staleTime, status, data, error);
            if (alreadyFresh) {
                return;
            }

            if (cached.promise) {
                status.value = "loading";
                try {
                    const result = await cached.promise;
                    if (!isCurrentKey(key)) return;
                    data.value = result ?? null;
                    error.value = null;
                    status.value = "success";
                } catch (err) {
                    if (!isCurrentKey(key)) return;
                    if (isAbortError(err)) {
                        // чужой подписчик оборвал общий промис — этому ключу
                        // ответ всё ещё нужен, идём за ним сами
                        await run(true, key);
                        return;
                    }
                    const normalized = normalizeError(err);
                    error.value = normalized;
                    status.value = "error";
                    options.onError?.(normalized);
                }
                return;
            }
        }

        abortActive();
        const controller = new AbortController();
        activeController = controller;

        status.value = "loading";
        const fetchPromise = options
            .fetcher({ signal: controller.signal })
            .then((result) => {
                queryCache.set(key, {
                    data: result,
                    updatedAt: Date.now(),
                    promise: undefined,
                    cacheTime,
                });
                // ПОЗДНИЙ ОТВЕТ НЕ ПИШЕТСЯ В ЧУЖОЙ КЛЮЧ. Кеш он пополняет — данные
                // верны для СВОЕГО ключа, — но экран уже смотрит на другой.
                if (isCurrentKey(key)) {
                    data.value = result;
                    error.value = null;
                    status.value = "success";
                }
                options.onSuccess?.(result);
                return result;
            })
            .catch((err) => {
                const normalized = normalizeError(err);
                if (!isAbortError(err) && isCurrentKey(key)) {
                    error.value = normalized;
                    status.value = "error";
                    options.onError?.(normalized);
                }
                throw normalized;
            })
            .finally(() => {
                if (activeController === controller) activeController = null;
                queryCache.set(key, { promise: undefined, cacheTime });
                queryCache.cleanup();
            });

        queryCache.set(key, {
            promise: fetchPromise,
            cacheTime,
        });

        await fetchPromise.catch(() => undefined);
    };

    stopWatch = watch(
        () => ({
            key: resolveKey(options.key),
            enabled: resolveEnabled(options.enabled),
        }),
        ({ key, enabled }) => {
            if (!enabled || !key) {
                abortActive();
                lastSerializedKey = null;
                lastKey = null;
                status.value = data.value === null ? "idle" : "success";
                return;
            }

            const serialized = serializeQueryKey(key);
            const keyChanged = serialized !== lastSerializedKey;
            
            // Отписываемся от предыдущего ключа и обрываем его запрос
            if (keyChanged) {
                abortActive();
                if (unsubscribeInvalidation) {
                    unsubscribeInvalidation();
                    unsubscribeInvalidation = null;
                }
            }

            lastSerializedKey = serialized;
            lastKey = key;

            if (keyChanged) {
                const cached = queryCache.get<TData>(key);
                if (!setStateFromCache(cached, options.staleTime ?? DEFAULT_STALE_TIME, status, data, error)) {
                    if (options.initialData !== undefined) {
                        data.value = resolveInitialData(options.initialData);
                        status.value = data.value === null ? "idle" : "success";
                        error.value = null;
                    } else {
                        data.value = null;
                        status.value = "idle";
                        error.value = null;
                    }
                }

                // Подписываемся на инвалидацию для нового ключа
                if (key) {
                    unsubscribeInvalidation = queryCache.subscribeToInvalidation(key, () => {
                        // При инвалидации перезапускаем запрос
                        void run(false, key);
                    });
                }
                
                // Вызываем run только если ключ изменился
                // Используем nextTick для уменьшения количества микрозадач
                void nextTick(() => {
                    void run(false, key);
                });
            } else {
                // Проверяем, не устарели ли данные, если ключ не изменился
                // Используем nextTick для отложенной проверки
                void nextTick(() => {
                    const cached = queryCache.get<TData>(key);
                    if (cached && !queryCache.isFresh(cached, options.staleTime ?? DEFAULT_STALE_TIME)) {
                        void run(false, key);
                    }
                });
            }
        },
        { immediate: true, flush: 'post' },
    );

    onScopeDispose(() => {
        stopWatch?.();
        unsubscribeInvalidation?.();
        abortActive();
    });

    const refresh = async (force = true): Promise<void> => {
        let key = lastKey ?? resolveKey(options.key);
        if (!key) {
            // Если ключ еще не готов, ждем следующего тика и пробуем снова
            // Используем nextTick вместо setTimeout для лучшей производительности
            await new Promise<void>((resolve) => {
                requestAnimationFrame(() => {
                    resolve();
                });
            });
            key = lastKey ?? resolveKey(options.key);
            if (!key) {
                return;
            }
        }
        await run(force, key);
    };

    const invalidate = (): void => {
        const key = lastKey ?? resolveKey(options.key);
        if (!key) return;
        queryCache.invalidate(key);
    };

    const setData = (updater: TData | ((previous: TData | null) => TData)): void => {
        const key = lastKey ?? resolveKey(options.key);
        if (!key) return;
        const nextValue =
            typeof updater === "function" ? (updater as (previous: TData | null) => TData)(data.value) : updater;

        data.value = nextValue;
        status.value = "success";
        error.value = null;

        queryCache.set(key, {
            data: nextValue,
            updatedAt: Date.now(),
        });
    };

    return {
        data,
        error,
        status,
        isLoading,
        isSuccess,
        isError,
        refresh,
        invalidate,
        setData,
    };
}

